import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { xml } from "@codemirror/lang-xml";
import { useCodeMirror } from "@uiw/react-codemirror";
import firebase from "firebase/app";
import { html as beautifyHtml } from "js-beautify";
import formatter from "xml-formatter";

import { Accordion, Button, Icon } from "semantic-ui-react";

import React, { useState } from "react";
import useAsyncFn from "react-use/lib/useAsyncFn";

import updateDistributionChannel from "astrid-firestore/src/api/distribution/updateDistributionChannel";

import FlexRow from "../../../../../components/Flex/FlexRow";

import metadataTemplateTypes from "../constants/metadataTemplateTypes";
import useTemplate from "../hooks/useTemplate";
import useTemplateHeaderTranslations from "../hooks/useTemplateHeaderTranslations";

const EXTENSIONS = {
	[metadataTemplateTypes.ONIX]: xml(),
	[metadataTemplateTypes.CUSTOM_XML]: xml(),
	[metadataTemplateTypes.EXCEL]: html(),
	[metadataTemplateTypes.JSON]: javascript(),
};

function formatHtml(html) {
	return beautifyHtml(html);
}

function formatXML(xml) {
	return formatter(xml, { collapseContent: true });
}

// an xml template stays well formed xml with handlebars in it, a json template does not stay
// valid json — {{#each}} around array items breaks every parser and js-beautify rewrites the
// block helpers into nested objects. leave json templates exactly as the author typed them
function formatNothing(template) {
	return template;
}

const FORMAT_FNS = {
	[metadataTemplateTypes.ONIX]: formatXML,
	[metadataTemplateTypes.CUSTOM_XML]: formatXML,
	[metadataTemplateTypes.EXCEL]: formatHtml,
	[metadataTemplateTypes.JSON]: (template) => template,
};

export default function MetadataEditor({ channel, templateType }) {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [template, setTemplate] = useTemplate(channel, templateType);
	const headers = useTemplateHeaderTranslations();

	const { setContainer } = useCodeMirror({
		container: document.getElementById("editor"),
		value: template,
		extensions: [EXTENSIONS[templateType]],
		onChange: (value) => {
			setTemplate(value);
		},
	});

	const [{ loading }, saveTemplate] = useAsyncFn(() => {
		return updateDistributionChannel(firebase, {
			ref: channel.ref,
			[templateType]: FORMAT_FNS[templateType](template),
		});
	}, [template, templateType]);

	return (
		<Accordion fluid styled>
			<Accordion.Title active={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)}>
				<Icon name="dropdown" />
				{headers[templateType]}
			</Accordion.Title>
			<Accordion.Content active={isCollapsed}>
				<div
					id="editor"
					ref={setContainer}
					style={{
						maxHeight: "800px",
						overflow: "auto",
						border: "1px solid #ccc",
						borderRadius: "8px",
						boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
					}}
				/>
				<FlexRow style={{ gap: 10, marginTop: 20, marginBottom: 10 }}>
					{FORMAT_FNS[templateType] !== formatNothing && (
						<Button
							secondary
							onClick={() => setTemplate(FORMAT_FNS[templateType](template))}
							content="Format template"
						/>
					)}
					<Button primary loading={loading} onClick={saveTemplate} content="Save template" />
				</FlexRow>
			</Accordion.Content>
		</Accordion>
	);
}
